import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Overlay, Popover } from 'react-bootstrap';
import { Clock, Info, X } from 'lucide-react';
import { platesLabel } from './contract';
import { customerGroups, detailText } from './detailsContract';

function PlatePopover({ candidate, title, label, icon: IconComponent, children }) {
  const id = useId();
  const target = useRef(null);
  const focusPopover = useCallback((node) => {
    if (node)
      requestAnimationFrame(() => {
        if (node.isConnected) node.focus({ preventScroll: true });
      });
  }, []);
  const [show, setShow] = useState(false);
  const close = () => {
    setShow(false);
    target.current?.focus({ preventScroll: true });
  };
  useEffect(() => {
    if (!show) return undefined;
    const escape = (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      setShow(false);
      target.current?.focus({ preventScroll: true });
    };
    window.addEventListener('keydown', escape, true);
    return () => window.removeEventListener('keydown', escape, true);
  }, [show]);
  return (
    <>
      <button
        type="button"
        className="tc-c-info"
        ref={target}
        title={title}
        aria-label={`${label} de ${platesLabel(candidate)}`}
        aria-haspopup="dialog"
        aria-expanded={show}
        aria-controls={show ? id : undefined}
        onClick={(event) => {
          event.stopPropagation();
          setShow((value) => !value);
        }}
      >
        <IconComponent size={15} aria-hidden="true" />
      </button>
      <Overlay
        ref={focusPopover}
        target={target.current}
        show={show}
        placement="right"
        flip
        rootClose
        onHide={() => setShow(false)}
        container={target.current?.closest('.modal')}
        transition={false}
        popperConfig={{
          modifiers: [
            {
              name: 'preventOverflow',
              options: { rootBoundary: 'viewport', altAxis: true, padding: 20 },
            },
          ],
        }}
      >
        <Popover
          id={id}
          tabIndex={-1}
          className="tc-c-popover"
          role="dialog"
          aria-label={title}
          onClick={(event) => event.stopPropagation()}
        >
          <Popover.Header as="div">
            <strong>{title}</strong>
            <button type="button" onClick={close} aria-label="Fechar informativo">
              <X size={16} aria-hidden="true" />
            </button>
          </Popover.Header>
          <Popover.Body>{children}</Popover.Body>
        </Popover>
      </Overlay>
    </>
  );
}
const assets = (candidate) => [candidate.vehicle, ...candidate.wagons];
const assetTitle = (asset) =>
  `${asset.assetType === 'wagon' ? 'Carreta' : 'Cavalo'} ${detailText(asset.plate)}`;
export function CandidateLink({ candidate }) {
  return (
    <PlatePopover
      candidate={candidate}
      title="Responsável da placa / filial"
      label="Consultar responsável"
      icon={Info}
    >
      {assets(candidate).map((asset) => (
        <section key={`${asset.assetType}:${asset.id}`} aria-label={assetTitle(asset)}>
          <h4>{assetTitle(asset)}</h4>
          <dl>
            <dt>Responsável</dt>
            <dd>{detailText(asset.settings?.responsibleName)}</dd>
            <dt>Telefone</dt>
            <dd>{detailText(asset.settings?.contactPhone)}</dd>
            <dt>Centro de Custo (CC)</dt>
            <dd>{detailText(asset.settings?.costCenterName)}</dd>
          </dl>
        </section>
      ))}
    </PlatePopover>
  );
}
export function CandidatePlate({ candidate }) {
  return (
    <PlatePopover
      candidate={candidate}
      title="Últimos 5 grupos de clientes"
      label="Consultar últimos clientes"
      icon={Clock}
    >
      {assets(candidate).map((asset) => {
        const groups = customerGroups(candidate, asset);
        return (
          <section key={`${asset.assetType}:${asset.id}`} aria-label={assetTitle(asset)}>
            <h4>{assetTitle(asset)}</h4>
            {groups === null ? (
              <p>Histórico não informado.</p>
            ) : groups.length === 0 ? (
              <p>Nenhum grupo de clientes no histórico consultado.</p>
            ) : (
              <ol>
                {groups.map((group) => (
                  // Só o grupo do cliente: data, horário e cidade ficam fora da lista.
                  <li key={group.groupId}>
                    <strong>{detailText(group.groupName)}</strong>
                  </li>
                ))}
              </ol>
            )}
          </section>
        );
      })}
    </PlatePopover>
  );
}
